// ✅ src/pages/Sales/EMIPaymentReceiptPrint.jsx
// UPDATED WITH SAFE LOCALSTORAGE HELPER
// Receives data from entry page via navigate state:
// navigate("/sales/emi-payment/receipt-print", {
//   state: { customerData: formData, paymentData: row, paymentList: gridMapped }
// });

import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";

const money = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (d) => {
  if (!d) return "";
  if (typeof d === "string" && d.includes("/")) return d.replaceAll("/", "-");
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

const EMIPaymentReceiptPrint = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const loginUserName =
    getLS("userName") || getLS("username") || getLS("loginUserName") || "";
  const defaultLocation = getLS("location") || "HEADOFFICE";

  const customerData = state?.customerData || null;
  const paymentData = state?.paymentData || null;

  const rows = useMemo(() => {
    if (!customerData || !paymentData) return [];

    const billed = pick(paymentData?.payableAmount, customerData?.valueOfAsset);
    const beforeBal = pick(paymentData?.balanceAmount);
    const paying = pick(paymentData?.paidAmount);
    const afterBal = (() => {
      const b = Number(beforeBal || 0);
      const p = Number(paying || 0);
      if (!Number.isNaN(b) && !Number.isNaN(p)) return b - p;
      return "";
    })();

    return [
      {
        sno: 1,
        date: fmtDate(paymentData?.date),
        billedAmount: billed,
        balanceAmount: beforeBal,
        payingAmount: paying,
        balanceAfterPayment: afterBal,
      },
    ];
  }, [customerData, paymentData]);

  const header = useMemo(() => {
    if (!customerData || !paymentData) return null;

    return {
      billNo: pick(paymentData?.billno, paymentData?.billNo),
      customerId: pick(customerData?.customerId, paymentData?.customerid),
      customerName: customerData?.customerName,
      productName: customerData?.productName,
      salesPerson: customerData?.salesPerson,
      totalSqFeet: customerData?.totalSqFeet,
      phase: pick(paymentData?.phase, ""),
      plotOrAssetCode: pick(customerData?.plotNoAssetCode, ""),
      location: pick(customerData?.location, defaultLocation),
      bookingDate: fmtDate(customerData?.dateOfAcquisition),
      assetValue: pick(customerData?.valueOfAsset, paymentData?.payableAmount),
      paymentMode: pick(paymentData?.paymentMode, "Cash"),
      status: pick(paymentData?.status, "Paid"),
    };
  }, [customerData, paymentData, defaultLocation]);

  const onPrint = () => window.print();

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .page { box-shadow: none !important; border: none !important; margin: 0 !important; }
        }
        body { background: #f4f5f7; }
      `}</style>

      <div className="no-print flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="px-3 py-2 rounded border bg-white">
            <FaArrowLeft className="inline mr-2" />
            Back
          </button>
          <button onClick={onPrint} className="px-4 py-2 rounded bg-black text-white">
            <FaPrint className="inline mr-2" />
            Print
          </button>
        </div>
      </div>

      <div className="flex justify-center p-3">
        <div
          className="page w-[794px] bg-white border border-gray-300 shadow-sm"
          style={{ minHeight: 1123 }}
        >
          <div className="p-5">
            {/* Header */}
            <div className="border border-black">
              <div className="grid grid-cols-[140px_1fr_160px] border-b border-black">
                <div className="border-r border-black flex items-center justify-center p-2">
                  <div className="w-full h-[70px] bg-gray-100 flex items-center justify-center text-xs text-gray-600">
                    LOGO
                  </div>
                </div>

                <div className="p-2 text-center">
                  <div className="font-semibold text-lg">Nissi Infotech</div>
                  <div className="text-sm font-medium">Nissi</div>
                  <div className="text-xs text-gray-700">
                    3/42-12, Nagappa colony , kavundampalayam , Coimbatore - 641030 . Tamil Nadu . India
                  </div>
                  <div className="text-xs text-gray-700 mt-1">
                    Phone No: 9003744644 &nbsp;&nbsp;&nbsp; Email: infraconsoft-implementation@nissiinfotech
                  </div>
                </div>

                <div className="border-l border-black flex items-center justify-center">
                  <div className="text-center font-semibold">
                    Payment <br /> Receipt
                  </div>
                </div>
              </div>

              <div className="py-3 text-center font-semibold">EMI Payment</div>

              <div className="grid grid-cols-2 border-t border-black">
                <div className="p-4">
                  <InfoRow label="Bill No" value={header?.billNo} />
                  <InfoRow label="Customer ID" value={header?.customerId} />
                  <InfoRow label="Customer Name" value={header?.customerName} />
                  <InfoRow label="Product Name" value={header?.productName} />
                  <InfoRow label="Sales Person" value={header?.salesPerson} />
                  <InfoRow label="Total Sq.Feet" value={header?.totalSqFeet} />
                  <InfoRow label="Phase" value={header?.phase} />
                </div>

                <div className="p-4 border-l border-black">
                  <InfoRow label="Plot No / Asset Code" value={header?.plotOrAssetCode} />
                  <InfoRow label="Location" value={header?.location} />
                  <InfoRow label="Date of Booking" value={header?.bookingDate} />
                  <InfoRow
                    label="Asset Value"
                    value={header?.assetValue ? `₹ ${money(header.assetValue)}` : ""}
                  />
                  <InfoRow label="Payment Mode" value={header?.paymentMode} />
                  <InfoRow label="Status" value={header?.status} />
                </div>
              </div>
            </div>

            <div className="h-8" />

            {/* Table */}
            <div className="border border-black px-6 py-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border border-black px-2 py-2 w-[70px]">S.No</th>
                    <th className="border border-black px-2 py-2 w-[160px]">Date</th>
                    <th className="border border-black px-2 py-2">Description</th>
                    <th className="border border-black px-2 py-2 w-[140px]">₹</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r) => (
                    <tr key={r.sno}>
                      <td className="border border-black px-2 py-2 text-center align-top">
                        {r.sno}
                      </td>
                      <td className="border border-black px-2 py-2 text-center align-top">
                        {r.date}
                      </td>

                      <td className="border border-black p-0">
                        <div className="grid grid-rows-4">
                          <DescCell text="Billed Amount" />
                          <DescCell text="Balance Amount" />
                          <DescCell text="Paying Amount" />
                          <DescCell text="Balance amount After Payment" />
                        </div>
                      </td>

                      <td className="border border-black p-0">
                        <div className="grid grid-rows-4">
                          <AmtCell value={money(r.billedAmount)} />
                          <AmtCell value={money(r.balanceAmount)} />
                          <AmtCell value={money(r.payingAmount)} />
                          <AmtCell value={money(r.balanceAfterPayment)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(!customerData || !paymentData) && (
                <div className="mt-3 text-xs text-red-600">
                  No print data received. Please open this page using Print from Entry screen.
                </div>
              )}
            </div>

            <div className="mt-8 flex items-end justify-between text-sm">
              <div>
                <div>{fmtDate(new Date())}</div>
                {finYear ? <div className="mt-1">Financial Year: {finYear}</div> : null}
                {branch ? <div className="mt-1">Branch: {branch}</div> : null}
              </div>

              <div className="text-right">
                <div className="font-semibold">Authorized Signatory</div>
                {loginUserName ? <div className="mt-1 text-xs">User: {loginUserName}</div> : null}
                {userId ? <div className="mt-1 text-xs">User ID: {userId}</div> : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="grid grid-cols-[150px_12px_1fr] gap-1 py-1 text-sm">
    <div className="font-medium">{label}</div>
    <div className="font-medium">:</div>
    <div className="break-words">{value || ""}</div>
  </div>
);

const DescCell = ({ text }) => (
  <div className="border-b border-black px-3 py-2 text-center font-medium last:border-b-0">
    {text}
  </div>
);

const AmtCell = ({ value }) => (
  <div className="border-b border-black px-3 py-2 text-right last:border-b-0">
    {value || ""}
  </div>
);

export default EMIPaymentReceiptPrint;